// src/components/menu/TopMenu.tsx

import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Dropdown } from "flowbite-react"; // Checkbox foi removido dos imports
import { useNavigate } from "react-router-dom";
import { useCompanyAccessStore } from "../../../states/store/CompanyAccess";
import {
  useCompanyStore,
  useCompanyStoreActions,
} from "../../../states/store/Company";
import MenuHamburguer from "./side-menu/MenuHamburguer";
import { BsBuildingDown, BsBuildingsFill } from "react-icons/bs";
import { GrDocumentConfig } from "react-icons/gr";
import { HiBellAlert } from "react-icons/hi2";
import "./TopMenu.scss";
import { IObjectCompanies } from "../../../../types/companyaccess.type";
import { useUser } from "../../../services/users.api";
import { AuthContext } from "../../auth/authContext/AuthContext";
import {
  getScoreOperation,
  ScopeOperationEntry,
} from "../../../services/vehicles.api";

type RegionName =
  | "SUDESTE"
  | "NORDESTE"
  | "CENTRO-OESTE"
  | "SUL"
  | "NORTE"
  | "OUTRAS";

type CompaniesByRegion = Record<
  string,
  {
    total: number;
    noteOperationSum: number;
    noteOperationCount: number;
    ufs: Record<
      string,
      {
        total: number;
        noteOperationSum: number;
        noteOperationCount: number;
        cidades: Array<
          IObjectCompanies & {
            totalVehicles: number;
            noteOperation: number | null;
          }
        >;
      }
    >;
  }
>;

type ScopeOperationLookup = {
  byCompanyId: Record<string, number>;
  byCityKey: Record<string, number>;
};

const REGION_ORDER: RegionName[] = [
  "SUDESTE",
  "NORDESTE",
  "CENTRO-OESTE",
  "SUL",
  "NORTE",
  "OUTRAS",
];

const UF_TO_REGION: Record<string, RegionName> = {
  AC: "NORTE",
  AL: "NORDESTE",
  AM: "NORTE",
  AP: "NORTE",
  BA: "NORDESTE",
  CE: "NORDESTE",
  DF: "CENTRO-OESTE",
  ES: "SUDESTE",
  GO: "CENTRO-OESTE",
  MA: "NORDESTE",
  MG: "SUDESTE",
  MS: "CENTRO-OESTE",
  MT: "CENTRO-OESTE",
  PA: "NORTE",
  PB: "NORDESTE",
  PE: "NORDESTE",
  PI: "NORDESTE",
  PR: "SUL",
  RJ: "SUDESTE",
  RN: "NORDESTE",
  RO: "NORTE",
  RR: "NORTE",
  RS: "SUL",
  SC: "SUL",
  SE: "NORDESTE",
  SP: "SUDESTE",
  TO: "NORTE",
};

const getRegionByUf = (uf?: string): RegionName => {
  if (!uf) {
    return "OUTRAS";
  }

  return UF_TO_REGION[uf.toUpperCase()] ?? "OUTRAS";
};

const formatVehicleCount = (value: number) =>
  new Intl.NumberFormat("pt-BR").format(value);

const formatOperationNote = (value: number | null) =>
  value === null ? "--" : value.toFixed(2).replace(".", ",");

const normalizeLocationKey = (value?: string) =>
  (value ?? "")
    .split("/")[0]
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const getOperationNoteClassName = (noteOperation: number | null) => {
  if (noteOperation === null) return "is-empty";
  if (noteOperation >= 9.5) return "is-top";
  if (noteOperation >= 9) return "is-good";
  if (noteOperation >= 7.5) return "is-warning";
  return "is-danger";
};

const resolveCompanyOperationNote = (
  company: IObjectCompanies,
  lookup: ScopeOperationLookup
) => {
  if (Object.prototype.hasOwnProperty.call(lookup.byCompanyId, company.companyId)) {
    return lookup.byCompanyId[company.companyId];
  }

  const cityKeys = [company.city, company.companyName].map(normalizeLocationKey);

  for (const cityKey of cityKeys) {
    if (Object.prototype.hasOwnProperty.call(lookup.byCityKey, cityKey)) {
      return lookup.byCityKey[cityKey];
    }
  }

  return null;
};

function TopMenu() {
  const navigate = useNavigate();
  const { auth, logout } = useContext(AuthContext);
  const { companyAccess } = useCompanyAccessStore();
  const { selectedCompanies, selectedRegion, selectedUf, filterMode } =
    useCompanyStore();
  const {
    toggleCompanySelection,
    setRegionSelection,
    setUfSelection,
    setAllSelection,
  } =
    useCompanyStoreActions();

  const [hasNotification] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [viewProducts, setViewProducts] = useState(false);
  const [scopeOperations, setScopeOperations] = useState<ScopeOperationEntry[]>(
    []
  );
  const user = useUser();

  const totalVehicles = useMemo(() => {
    return (
      companyAccess?.companies?.reduce(
        (sum, company) => sum + (company.statistics?.totalVehicles || 0),
        0
      ) ?? 0
    );
  }, [companyAccess]);

  useEffect(() => {
    if (!auth?.user?.roles) return;
    if (auth.user.roles.length > 0) {
      setRoles(auth.user.roles);
      if (
        auth.user.roles.includes("admin") ||
        auth.user.roles.includes("stockist")
      ) {
        setViewProducts(true);
      }
    }
  }, [auth?.user?.roles]);

  useEffect(() => {
    if (!auth?.user?.email) return;

    let isMounted = true;

    const loadScopeOperations = async () => {
      const response = await getScoreOperation(undefined, { skipScope: true });

      if (!isMounted) return;

      if (response?.ok && Array.isArray(response.data)) {
        setScopeOperations(response.data);
        return;
      }

      setScopeOperations([]);
    };

    loadScopeOperations();

    return () => {
      isMounted = false;
    };
  }, [auth?.user?.email]);

  const scopeOperationLookup = useMemo<ScopeOperationLookup>(() => {
    const byCompanyId: Record<string, number> = {};
    const cityTotals: Record<string, { sum: number; count: number }> = {};

    scopeOperations.forEach((operation) => {
      const companyKey = String(operation?.companyId ?? "").trim();
      const noteOperation = Number(operation?.noteOperation ?? 0);

      if (!companyKey || !Number.isFinite(noteOperation)) {
        return;
      }

      byCompanyId[companyKey] = noteOperation;

      const cityKey = normalizeLocationKey(companyKey);
      if (!cityTotals[cityKey]) {
        cityTotals[cityKey] = { sum: 0, count: 0 };
      }

      cityTotals[cityKey].sum += noteOperation;
      cityTotals[cityKey].count += 1;
    });

    const byCityKey = Object.entries(cityTotals).reduce<Record<string, number>>(
      (acc, [cityKey, values]) => {
        acc[cityKey] = Number((values.sum / values.count).toFixed(2));
        return acc;
      },
      {}
    );

    return { byCompanyId, byCityKey };
  }, [scopeOperations]);

  const companyAccessByRegion = useMemo(() => {
    const grouped: CompaniesByRegion = {};

    if (Array.isArray(companyAccess?.companies)) {
      companyAccess.companies.forEach((item) => {
        const cityParts = item.companyName?.split("/") ?? [];
        const uf = cityParts[1]?.toUpperCase() ?? "SEM_UF";
        const region = getRegionByUf(uf);
        const total = item?.statistics?.totalVehicles ?? 0;
        const noteOperation = resolveCompanyOperationNote(
          item,
          scopeOperationLookup
        );

        if (!grouped[region]) {
          grouped[region] = {
            total: 0,
            noteOperationSum: 0,
            noteOperationCount: 0,
            ufs: {},
          };
        }

        if (!grouped[region].ufs[uf]) {
          grouped[region].ufs[uf] = {
            total: 0,
            noteOperationSum: 0,
            noteOperationCount: 0,
            cidades: [],
          };
        }

        grouped[region].ufs[uf].cidades.push({
          ...item,
          totalVehicles: total,
          noteOperation,
        });
        grouped[region].ufs[uf].total += total;
        grouped[region].total += total;

        if (noteOperation !== null) {
          grouped[region].ufs[uf].noteOperationSum += noteOperation;
          grouped[region].ufs[uf].noteOperationCount += 1;
          grouped[region].noteOperationSum += noteOperation;
          grouped[region].noteOperationCount += 1;
        }
      });
    }

    return REGION_ORDER.reduce<CompaniesByRegion>((acc, region) => {
      if (grouped[region]) {
        acc[region] = grouped[region];
      }
      return acc;
    }, {});
  }, [companyAccess, scopeOperationLookup]);

  const totalOperationNote = useMemo(() => {
    const companies = companyAccess?.companies ?? [];

    if (companies.length === 0) {
      return null;
    }

    const notes = companies
      .map((company) => resolveCompanyOperationNote(company, scopeOperationLookup))
      .filter((note): note is number => note !== null);

    if (notes.length === 0) {
      return null;
    }

    return Number(
      (notes.reduce((sum, note) => sum + note, 0) / notes.length).toFixed(2)
    );
  }, [companyAccess, scopeOperationLookup]);

  const getLocationLabel = () => {
    if (filterMode === "all") {
      return "TODAS AS CIDADES";
    }
    if (filterMode === "region" && selectedRegion) {
      return `TODAS DE ${selectedRegion}`;
    }
    if (filterMode === "uf" && selectedUf) {
      return `TODAS DE ${selectedUf}`;
    }
    if (filterMode === "cities") {
      if (selectedCompanies.length === 1) {
        return selectedCompanies[0].city || "Cidade Selecionada";
      }
      return `MÚLTIPLAS CIDADES (${selectedCompanies.length})`;
    }
    return "Selecione a empresa";
  };

  const isCompanySelected = (companyId: string) => {
    return (
      filterMode === "cities" &&
      selectedCompanies.some((c) => c.companyId === companyId)
    );
  };

  const renderMetrics = (total: number, noteOperation: number | null) => (
    <div className="menu-company-metrics">
      <span className="menu-company-total">({formatVehicleCount(total)})</span>
      <span
        className={`menu-company-note-badge ${getOperationNoteClassName(
          noteOperation
        )}`}
      >
        {formatOperationNote(noteOperation)}
      </span>
    </div>
  );

  return (
    <header className="top-menu">
      <nav>
        <div className="linha-superior"></div>
        <div className="espacamento"></div>
        <div className="menuhamburguer">
          <MenuHamburguer />
        </div>
        <div className="logo-name">
          <img
            onClick={() => navigate("/")}
            alt="Locavibe logo"
            className="logo-img"
            src="/escudo_vermelho.png"
          />
          <div className="locavibe-backoffice" onClick={() => navigate("/")}>
            <div className="flex justify-center">
              <h2 className="loca ">loca</h2>
              <h2 className="vibe">vibe</h2>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          {viewProducts && (
            <div className="bell-alert">
              <div className="relative group text-center flex">
                <HiBellAlert
                  className={`text-[25px] ${
                    hasNotification
                      ? "text-redPrimary animate-pulse"
                      : "text-white"
                  }`}
                />
                {hasNotification && (
                  <div className="bellAlert-message group-hover:block">
                    Alerta! Peças no estoque abaixo da quantidade mínima!
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="menu-company-names">
            <Dropdown
              className="dropdown-user-info"
              inline
              label={
                <div>
                  <div className="menu-dropdown">{getLocationLabel()}</div>
                  <div className="menu-dropdown-icon">
                    <BsBuildingDown />
                  </div>
                </div>
              }
            >
              {(roles.some((role) => role === "admin") ||
                companyAccess?.viewAll === true) && (
                <Dropdown.Item
                  key="todas-empresas"
                  className="px-[0.5rem] py-0"
                  onClick={setAllSelection}
                >
                  <div
                    className={`menu-company-list ${
                      filterMode === "all" ? "selected-company" : ""
                    }`}
                  >
                    <div className="menu-company-content">
                      <div className="menu-company-label">
                        <BsBuildingsFill />
                        <span>TODAS</span>
                      </div>
                      {renderMetrics(totalVehicles, totalOperationNote)}
                    </div>
                  </div>
                </Dropdown.Item>
              )}
              <div className="dropdown-scroll-container">
                {Object.entries(companyAccessByRegion).map(
                  ([region, { total, noteOperationSum, noteOperationCount, ufs }]) => (
                    <Fragment key={region}>
                      <Dropdown.Header
                        className="px-0 py-0 header-company-region"
                        onClick={() => setRegionSelection(region)}
                      >
                        <div
                          className={`menu-company-region ${
                            selectedRegion === region ? "selected-company" : ""
                          }`}
                        >
                          <div className="menu-company-content">
                            <span>{region}</span>
                            {renderMetrics(
                              total,
                              noteOperationCount > 0
                                ? Number(
                                    (noteOperationSum / noteOperationCount).toFixed(2)
                                  )
                                : null
                            )}
                          </div>
                        </div>
                      </Dropdown.Header>
                      {Object.entries(ufs).map(
                        ([
                          uf,
                          {
                            total,
                            noteOperationSum,
                            noteOperationCount,
                            cidades,
                          },
                        ]) => (
                        <Fragment key={uf}>
                          <Dropdown.Header
                            className={"px-0 py-0 header-company-uf"}
                            onClick={() => setUfSelection(uf)}
                          >
                            <div
                              className={`menu-company-uf ${
                                selectedUf === uf ? "selected-company" : ""
                              }`}
                            >
                              <div className="menu-company-content">
                                <span>{uf}</span>
                                {renderMetrics(
                                  total,
                                  noteOperationCount > 0
                                    ? Number(
                                        (
                                          noteOperationSum / noteOperationCount
                                        ).toFixed(2)
                                      )
                                    : null
                                )}
                              </div>
                            </div>
                          </Dropdown.Header>
                          {cidades.map((item) => (
                            <Dropdown.Item key={item.companyId} className="p-0">
                              <div
                                className={`w-full pl-2 pr-[0.5rem] py-1.5 ${
                                  isCompanySelected(item.companyId)
                                    ? "selected-company"
                                    : ""
                                }`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCompanySelection(item);
                                }}
                              >
                                <div
                                  className="menu-company-list"
                                >
                                  <div className="menu-company-content">
                                    <div className="menu-company-label">
                                      <span>{item.companyName}</span>
                                    </div>
                                    {renderMetrics(
                                      item.totalVehicles,
                                      item.noteOperation
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Dropdown.Item>
                          ))}
                        </Fragment>
                      ))}
                    </Fragment>
                  )
                )}
              </div>
            </Dropdown>
          </div>
          <div className="menu-user-info">
            <Dropdown
              className="dropdown-user-info"
              inline
              label={
                <div>
                  <div className="menu-dropdown">{user.name}</div>
                  <div className="menu-dropdown-icon">
                    <GrDocumentConfig />
                  </div>
                </div>
              }
            >
              <div className="user-info">
                <Dropdown.Header>
                  <div>{user.name}</div>
                  <div>{user.email}</div>
                </Dropdown.Header>
              </div>
              <Dropdown.Item className="pt-0 pb-0">
                <div className="user-config">Minhas configurações</div>
              </Dropdown.Item>
              <Dropdown.Item className="pt-0 pb-0" onClick={() => logout()}>
                <div className="user-config">Sair</div>
              </Dropdown.Item>
            </Dropdown>
          </div>
        </div>
        <div className="espacamento"></div>
      </nav>
    </header>
  );
}

export default TopMenu;
